// Code generated by erp-api-gateway/cmd/contract; DO NOT EDIT.
// Contract SHA-256: 89c920f8e404aa0740e580ffe322895e8f684634e5b36cc677e304a84bce2a0a

import { z } from "zod/v3";

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue =
  | JsonPrimitive
  | { readonly [key: string]: JsonValue }
  | readonly JsonValue[];
export type JsonObject = Readonly<Record<string, JsonValue>>;

export const jsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ]),
);
export const jsonObjectSchema = z.record(jsonValueSchema);
export const emptyObjectSchema = z.object({}).strict();
export const emptySchema = z.undefined();
export const uuidSchema = z.string().uuid();
export const timestampSchema = z.string().datetime({ offset: true });
export const messageSchema = z.object({ message: z.string() });
export const dataObjectSchema = z.object({ data: jsonObjectSchema });
export const dataArraySchema = z.object({ data: z.array(jsonObjectSchema) });

export const pageMetaSchema = z.object({
  current_page: z.number().int().positive(),
  from: z.number().int().nullable(),
  last_page: z.number().int().nonnegative(),
  path: z.string(),
  per_page: z.number().int().positive(),
  to: z.number().int().nullable(),
  total: z.number().int().nonnegative(),
});

export const cursorMetaSchema = z.object({
  path: z.string(),
  per_page: z.number().int().positive(),
  next_cursor: z.string().nullable(),
  prev_cursor: z.string().nullable(),
});

export const pageLinksSchema = z.record(z.string().nullable());
export const cursorLinksSchema = z.object({
  next: z.string().nullable(),
  prev: z.string().nullable(),
});

export const twoFactorStateSchema = z.object({
  enabled: z.boolean(),
  required: z.boolean(),
  setup_required: z.boolean(),
});
export const userStatusSchema = z.enum([
  "active",
  "pending_activation",
  "invited",
  "blocked",
  "disabled",
  "business_trip",
  "vacation",
  "sick_leave",
  "terminated",
  "archived",
]);

export const userSummarySchema = z.object({
  id: uuidSchema,
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  display_name: z.string().nullable(),
  email: z.string().email(),
  status: userStatusSchema,
  avatar_download_path: z.string().nullable(),
});

export const identityUserSchema = z.object({
  id: uuidSchema,
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  display_name: z.string(),
  email: z.string().email(),
  email_verified_at: timestampSchema.nullable(),
  phone: z.string().nullable(),
  avatar: z
    .object({
      file_id: uuidSchema,
      filename: z.string(),
      mime_type: z.enum(["image/jpeg", "image/png", "image/webp"]),
      size: z.number().int().positive(),
      download_path: z.string().startsWith("/api/v1/files/"),
      updated_at: timestampSchema.nullable(),
    })
    .nullable(),
  status: userStatusSchema,
  department: z.string().nullable(),
  position: z.string().nullable(),
  language: z.string(),
  timezone: z.string(),
  two_factor: twoFactorStateSchema,
  last_login_at: timestampSchema.nullable(),
  roles: z.array(z.string()),
  // Роли, выданные без организации: действуют во всех организациях (ES-87).
  holding_wide_roles: z.array(z.string()).optional(),
  // Только в собственной карточке: чужих системных администраторов API не
  // раскрывает.
  is_system_administrator: z.boolean().optional(),
  permissions: z.array(z.string()),
  scopes: z.array(jsonObjectSchema),
  current_scope: jsonObjectSchema.nullable(),
  organization_mode: z.enum(["single", "all"]),
});
export const identityUserResponseSchema = z.object({ data: identityUserSchema });
export const authenticatedResponseSchema = z.object({
  data: z.object({
    authenticated: z.literal(true),
    two_factor_challenge: z.literal(false),
    user: identityUserSchema,
  }),
});
export const loginResponseSchema = z.union([
  authenticatedResponseSchema,
  z.object({ two_factor: z.literal(true) }),
]);
export const identitySessionSchema = z.object({
  id: uuidSchema,
  ip_address: z.string().nullable(),
  user_agent: z.string(),
  last_activity_at: timestampSchema,
  current: z.boolean(),
});
export const identitySessionsResponseSchema = z.object({
  data: z.array(identitySessionSchema),
});

export const accessScopeSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  type: z.enum(["core", "kpo"]),
  name: z.string(),
  is_active: z.boolean(),
  created_at: timestampSchema.nullable(),
  updated_at: timestampSchema.nullable(),
});
export type OrganizationNode = {
  id: string;
  type: "core" | "kpo" | "department";
  code: string;
  name: string;
  is_active: boolean;
  scope_id: string;
  parent_id: string | null;
  children: OrganizationNode[];
};
export const organizationNodeSchema: z.ZodType<OrganizationNode> = z.lazy(() =>
  z.object({
    id: uuidSchema,
    type: z.enum(["core", "kpo", "department"]),
    code: z.string(),
    name: z.string(),
    is_active: z.boolean(),
    scope_id: uuidSchema,
    parent_id: uuidSchema.nullable(),
    children: z.array(organizationNodeSchema),
  }),
);
export const hierarchyNodeSchema = z.object({
  id: uuidSchema,
  manager_id: uuidSchema.nullable(),
  display_name: z.string(),
  email: z.string().email(),
  status: userStatusSchema,
  position: z.string().nullable(),
  is_system_administrator: z.boolean(),
  avatar_download_path: z.string().nullable(),
});
export const hierarchyReachSchema = z.object({
  unrestricted: z.boolean(),
  delegable_ids: z.array(uuidSchema),
});
export const departmentSchema = z.object({
  id: uuidSchema,
  scope_id: uuidSchema,
  parent_id: uuidSchema.nullable(),
  code: z.string(),
  name: z.string(),
});
export const membershipSchema = z.object({
  user: userSummarySchema,
  id: uuidSchema,
  scope_id: uuidSchema,
  user_id: uuidSchema,
  department_id: uuidSchema.nullable(),
  position: z.string().nullable(),
  is_active: z.boolean(),
});
export const permissionSchema = z.object({
  key: z.string(),
  group: z.string(),
  description: z.string(),
  is_system: z.boolean(),
});
export const roleSchema = z.object({
  id: uuidSchema,
  slug: z.string(),
  name: z.string(),
  is_system: z.boolean(),
  permissions: z.array(z.string()),
});
const accessRightsTransferBodyBaseSchema = z.object({
  source_user_id: uuidSchema,
  target_user_id: uuidSchema,
  scope_type: z.enum(["access_scope", "global"]),
  access_scope_id: uuidSchema.nullable().optional(),
  mode: z.enum(["full", "partial"]),
  source_action: z.enum(["retain", "revoke"]),
  remaining_source_rights_action: z.enum(["retain", "revoke"]).nullable().optional(),
  role_ids: z.array(uuidSchema).max(100).optional(),
  permission_override_keys: z.array(z.string().min(1).max(128)).max(200).optional(),
  expires_at: timestampSchema.nullable().optional(),
  reason: z.string().min(3).max(2000),
}).strict();

function buildAccessRightsTransferBodySchema(requireRemainingDecision: boolean) {
  return accessRightsTransferBodyBaseSchema.superRefine((value, context) => {
    if (value.source_user_id === value.target_user_id) {
      context.addIssue({ code: "custom", path: ["target_user_id"], message: "Source and target must differ" });
    }
    if (value.scope_type === "access_scope" && !value.access_scope_id) {
      context.addIssue({ code: "custom", path: ["access_scope_id"], message: "Scope is required" });
    }
    if (value.scope_type === "global" && value.access_scope_id != null) {
      context.addIssue({ code: "custom", path: ["access_scope_id"], message: "Scope is prohibited for global transfer" });
    }
    if (value.mode === "full" && (value.role_ids !== undefined || value.permission_override_keys !== undefined)) {
      context.addIssue({ code: "custom", path: ["mode"], message: "Full transfer cannot contain a partial selection" });
    }
    if (value.mode === "partial" && !(value.role_ids?.length || value.permission_override_keys?.length)) {
      context.addIssue({ code: "custom", path: ["mode"], message: "Partial transfer requires a selection" });
    }
    const needsDecision = value.mode === "partial" && value.source_action === "revoke";
    if (requireRemainingDecision && needsDecision && value.remaining_source_rights_action == null) {
      context.addIssue({ code: "custom", path: ["remaining_source_rights_action"], message: "Decision is required" });
    }
    if (!needsDecision && value.remaining_source_rights_action != null) {
      context.addIssue({ code: "custom", path: ["remaining_source_rights_action"], message: "Decision is not applicable" });
    }
  });
}

export const accessRightsTransferPreviewBodySchema = buildAccessRightsTransferBodySchema(false);
export const accessRightsTransferBodySchema = buildAccessRightsTransferBodySchema(true);
export const accessRightsTransferRoleSchema = z.object({
  id: uuidSchema,
  slug: z.string(),
  name: z.string(),
  is_system: z.boolean(),
  expires_at: timestampSchema.nullable(),
});
export const accessRightsTransferOverrideSchema = z.object({
  permission_key: z.string(),
  decision: z.enum(["allow", "deny"]),
  expires_at: timestampSchema.nullable(),
});
export const accessRightsTransferAssignmentsSchema = z.object({
  roles: z.array(accessRightsTransferRoleSchema),
  permission_overrides: z.array(accessRightsTransferOverrideSchema),
});
export const accessRightsTransferPlanSchema = z.object({
  source_user_id: uuidSchema,
  target_user_id: uuidSchema,
  scope: z.object({
    type: z.enum(["access_scope", "global"]),
    id: uuidSchema.nullable(),
    name: z.string().nullable(),
  }),
  mode: z.enum(["full", "partial"]),
  source_action: z.enum(["retain", "revoke"]),
  remaining_source_rights_action: z.enum(["retain", "revoke"]).nullable(),
  expires_at: timestampSchema.nullable(),
  reason: z.string(),
  selected: accessRightsTransferAssignmentsSchema,
  remaining_source_rights: accessRightsTransferAssignmentsSchema,
  non_transferable: accessRightsTransferAssignmentsSchema,
  requires_remaining_source_rights_action: z.boolean(),
});
export const accessRightsTransferResultSchema = accessRightsTransferPlanSchema.extend({
  id: uuidSchema,
  status: z.literal("completed"),
  affected_target_assignments: z.object({
    role_ids: z.array(uuidSchema),
    permission_override_keys: z.array(z.string()),
  }),
  after: z.object({
    source: accessRightsTransferAssignmentsSchema,
    target: accessRightsTransferAssignmentsSchema,
  }),
});
export const accessChangeSchema = z.object({
  id: uuidSchema,
  actor_id: uuidSchema.nullable(),
  scope_id: uuidSchema.nullable(),
  action: z.string(),
  object_type: z.string(),
  object_id: z.string().nullable(),
  before: z.record(jsonValueSchema),
  after: z.record(jsonValueSchema),
  request_id: uuidSchema.nullable(),
  correlation_id: uuidSchema.nullable(),
  created_at: timestampSchema,
});
export const userChangeSchema = z.object({
  id: uuidSchema,
  actor_id: uuidSchema.nullable(),
  action: z.string(),
  before: z.record(jsonValueSchema),
  after: z.record(jsonValueSchema),
  request_id: uuidSchema.nullable(),
  correlation_id: uuidSchema.nullable(),
  created_at: timestampSchema,
});
export const criticalAdministratorSchema = z.object({
  public_id: uuidSchema,
  display_name: z.string(),
  email: z.string().email(),
  status: userStatusSchema,
  blocked_at: timestampSchema.nullable(),
});
export const adminUserSchema = identityUserSchema.extend({
  created_by: uuidSchema.nullable(),
  created_at: timestampSchema.nullable(),
  updated_at: timestampSchema.nullable(),
  blocked_at: timestampSchema.nullable(),
  deleted_at: timestampSchema.nullable(),
});

export const auditEventSchema = z.object({
  id: uuidSchema,
  actor: z.object({ id: uuidSchema, display_name: z.string() }).nullable(),
  scope_id: uuidSchema.nullable(),
  action: z.string(),
  category: z.enum([
    "authentication",
    "authorization",
    "user_management",
    "role_management",
    "configuration",
    "security",
    "file_access",
    "module_management",
    "system",
  ]),
  object_type: z.string().nullable(),
  object_id: z.string().nullable(),
  // Разрешённый объект: вид и человеческое название. Собирается на стороне
  // ERP Core одним запросом на страницу журнала, чтобы интерфейс не ходил за
  // названием каждого файла и документа по отдельности.
  object: z
    .object({ kind: z.string(), id: z.string(), label: z.string().nullable() })
    .nullable(),
  ip_address: z.string().nullable(),
  user_agent: z.string().nullable(),
  request_id: uuidSchema.nullable(),
  correlation_id: uuidSchema.nullable(),
  source: z.enum([
    "user",
    "system",
    "scheduler",
    "queue",
    "integration",
    "administrator",
  ]),
  old_values: z.record(z.unknown()),
  new_values: z.record(z.unknown()),
  metadata: z.record(z.unknown()),
  result: z.enum(["success", "failure", "denied"]),
  severity: z.enum(["debug", "info", "warning", "error", "critical"]),
  occurred_at: timestampSchema,
});
export const auditExportSchema = z.object({
  id: uuidSchema,
  status: z.enum(["pending", "processing", "ready", "failed"]),
  format: z.literal("csv"),
  filters: z.record(z.unknown()),
  checksum: z.string().nullable(),
  size_bytes: z.number().int().nonnegative().nullable(),
  error_code: z.string().nullable(),
  created_at: timestampSchema.nullable(),
  completed_at: timestampSchema.nullable(),
  expires_at: timestampSchema.nullable(),
  download_url: z.string().nullable(),
});

export const filePurposeSchema = z.enum([
  "avatar",
  "module",
  "public_content",
  "temporary",
  "audit_export",
]);
// Папка раздела «Файлы». Принадлежит организации, может быть показана
// соседним КПО, и can_manage говорит интерфейсу, показывать ли кнопки правки.
export const fileFolderSchema = z.object({
  id: uuidSchema,
  parent_id: uuidSchema.nullable(),
  scope_id: uuidSchema.nullable(),
  name: z.string(),
  can_manage: z.boolean(),
  created_at: timestampSchema.nullable(),
  updated_at: timestampSchema.nullable(),
});
// Одно место, где используется файл: версия документа, вложение письма,
// вложение обсуждения или материал.
export const fileUsageSchema = z.object({
  kind: z.enum(["document_version", "mail_attachment", "comment_attachment", "material"]),
  title: z.string(),
  document_id: uuidSchema.nullable(),
});
// КПО, которому папка открыта дополнительно к организации-владельцу.
export const fileFolderScopeSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
});
export const fileRecordSchema = z.object({
  id: uuidSchema,
  owner_id: uuidSchema,
  scope_id: uuidSchema.nullable(),
  // Папка раздела «Файлы». Пусто — файл лежит в корне.
  folder_id: uuidSchema.nullable(),
  folder_name: z.string().nullable(),
  // Откуда файл взялся и сколько живых ссылок его держат. used_in заполняется
  // только тому, кто вправе файл удалить.
  origin: z.enum(["manual", "email_attachment", "comment_attachment", "material"]),
  reference_count: z.number().int().nonnegative(),
  used_in: z.array(fileUsageSchema),
  purpose: filePurposeSchema,
  module_key: z.string().nullable(),
  entity_type: z.string().nullable(),
  entity_id: z.string().nullable(),
  filename: z.string(),
  mime_type: z.string(),
  size: z.number().int().positive(),
  sha256: z.string().length(64).nullable(),
  status: z.enum(["pending", "ready", "rejected", "expired"]),
  created_at: timestampSchema,
  confirmed_at: timestampSchema.nullable(),
  deleted_at: timestampSchema.nullable(),
  can_delete: z.boolean(),
  original_name: z.string().optional(),
  can_rename: z.boolean().optional(),
});
// Адрес загрузки. Мелкий файл уходит в хранилище одним подписанным PUT,
// крупный — частями по TUS. Протокол назван явно, чтобы клиент не угадывал
// его по размеру файла; chunk_bytes задаёт размер части и заполняется
// только для TUS.
//
// Для signed_put это полный адрес хранилища, для tus — путь на том же API,
// куда клиент уже ходит: сессия выдана на этот домен, и досылать части на
// соседний нельзя.
export const signedUploadSchema = z.object({
  protocol: z.enum(["signed_put", "tus"]),
  url: z.string().min(1),
  headers: z.record(z.string()),
  chunk_bytes: z.number().int().nullable().optional(),
});
export const signedDownloadSchema = z.object({
  url: z.string().url(),
  expires_at: timestampSchema,
});

// Предпросмотр дополнительно сообщает тип содержимого по ссылке: офисные
// документы отдаются сконвертированным PDF. Поле необязательное, чтобы
// клиент работал и со старым ответом бэкенда.
export const signedPreviewSchema = z.object({
  url: z.string().url(),
  expires_at: timestampSchema,
  mime_type: z.string().optional(),
});

export const modulePermissionSchema = z.object({
  key: z.string(),
  description: z.string(),
});
export const moduleBackendRouteSchema = z.object({
  method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]),
  path: z.string(),
});
export const moduleMenuItemSchema = z.object({
  label: z.string(),
  path: z.string(),
  icon: z.string().optional(),
  order: z.number().int().optional(),
  permission: z.string().optional(),
});
export const moduleDefinitionSchema = z.object({
  id: uuidSchema,
  key: z.string(),
  display_name: z.string(),
  version: z.string(),
  state: z.string(),
  permissions: z.array(modulePermissionSchema),
  backend_routes: z.array(moduleBackendRouteSchema),
  frontend_route_prefix: z.string().nullable(),
  menu: z.array(moduleMenuItemSchema),
  settings_schema: z.record(z.unknown()),
  health_state: z.enum(["healthy", "degraded", "unavailable", "unknown"]),
  enabled: z.boolean(),
});
export const moduleNavigationItemSchema = moduleMenuItemSchema.extend({
  module_key: z.string(),
  frontend_route_prefix: z.string().nullable(),
});
export const moduleSettingsSchema = z.object({
  module_key: z.string(),
  enabled: z.boolean(),
  settings: z.record(z.unknown()),
  schema: z.record(z.unknown()),
});

export const systemNotificationSchema = z.object({
  id: uuidSchema,
  type: z.string(),
  priority: z.enum(["low", "normal", "high", "critical"]),
  title: z.string(),
  body: z.string(),
  link_url: z.string().nullable(),
  object_type: z.string().nullable(),
  object_id: z.string().nullable(),
  payload: z.record(z.unknown()),
  read_at: timestampSchema.nullable(),
  expires_at: timestampSchema.nullable(),
  created_at: timestampSchema,
});
// Настройка одного события. `label` и `group` приходят с сервера, чтобы новое
// событие появлялось в настройках без правок интерфейса. `configured` говорит,
// свои это значения или унаследованные от общего правила.
export const notificationPreferenceSchema = z.object({
  type: z.string(),
  label: z.string(),
  group: z.string(),
  in_app: z.boolean(),
  realtime: z.boolean(),
  web_push: z.boolean(),
  configured: z.boolean(),
});
// В запросе на сохранение отправляются только сами значения: название и раздел
// принадлежат серверу.
export const notificationPreferenceInputSchema = z.object({
  type: z.string(),
  in_app: z.boolean(),
  realtime: z.boolean(),
  web_push: z.boolean(),
});
export const pushSubscriptionSchema = z.object({
  id: uuidSchema,
  // Отпечаток адреса подписки: сам адрес наружу не отдаётся, но браузеру нужно
  // узнать среди устройств своё.
  endpoint_fingerprint: z.string(),
  device_name: z.string().nullable(),
  content_encoding: z.enum(["aesgcm", "aes128gcm"]).nullable(),
  created_at: timestampSchema.nullable(),
  last_used_at: timestampSchema.nullable(),
});
export const realtimeConfigSchema = z.object({
  enabled: z.boolean(),
  key: z.string().nullable(),
  host: z.string().nullable(),
  port: z.number().int().positive(),
  scheme: z.enum(["http", "https"]),
  channel: z.literal("private-users.{user_id}"),
  auth_endpoint: z.string(),
  vapid_public_key: z.string().nullable(),
});
export const queueStateSchema = z.object({
  queue: z.string(),
  label: z.string(),
  worker: z.string(),
  depth: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  delayed: z.number().int().nonnegative(),
  reserved: z.number().int().nonnegative(),
  oldest_age_seconds: z.number().int().nonnegative(),
  status: z.enum(["healthy", "lagging", "stalled"]),
});
export const queueHealthSchema = z.object({
  status: z.enum(["healthy", "degraded"]),
  queued: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  // Ошибки за последние сутки. Общий счётчик копит историю и сам по себе
  // ничего не говорит о текущем состоянии стенда.
  failed_recent: z.number().int().nonnegative(),
  lag_seconds: z.number().int().nonnegative(),
  queues: z.array(queueStateSchema),
});
export const outboxHealthSchema = z.object({
  status: z.enum(["healthy", "degraded"]),
  pending: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  oldest_pending_at: timestampSchema.nullable(),
});

export const documentWorkflowPageMetaSchema = z.object({
  current_page: z.number().int().positive(),
  per_page: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  last_page: z.number().int().nonnegative(),
});
export const documentWorkflowCursorMetaSchema = z.object({
  next_cursor: z.string().nullable(),
});
export const documentRegistrationSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  date: z.string(),
  journal: z.string(),
});
export const documentCounterpartySummarySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  inn: z.string().nullable(),
  kpp: z.string().nullable(),
});
export const documentWorkflowCategorySummarySchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
});
export const documentWorkflowCategorySchema = documentWorkflowCategorySummarySchema.extend({
  description: z.string().nullable(),
  is_active: z.boolean(),
  is_system: z.boolean(),
});
export const documentWorkflowFolderSummarySchema = z.object({
  id: uuidSchema,
  name: z.string(),
});
export const documentWorkflowFolderSchema = documentWorkflowFolderSummarySchema.extend({
  parent_id: uuidSchema.nullable(),
  created_at: timestampSchema.nullable(),
  updated_at: timestampSchema.nullable(),
});
export const documentWorkflowFolderGrantSchema = z.object({
  folder_id: uuidSchema,
  user_id: uuidSchema,
  access_level: z.enum(["view", "edit", "manage"]),
});
export const documentWorkflowScopeAccessSchema = z.object({
  id: uuidSchema,
  code: z.string(),
  name: z.string(),
  access_level: z.enum(["read", "write", "manage"]),
});
export const documentWorkflowDocumentMoveSchema = z.object({
  folder_id: uuidSchema.nullable(),
});
export const documentWorkflowMaterialSchema = z.object({
  id: uuidSchema,
  title: z.string(),
  purpose: z.enum(["attachment", "supplementary", "working", "reference"]),
  folder_id: uuidSchema.nullable(),
  file: z.object({
    id: uuidSchema,
    name: z.string(),
    mime: z.string().nullable(),
    size: z.number().int().nonnegative().nullable(),
    sha256: z.string().nullable(),
    status: z.string(),
  }),
  created_at: timestampSchema.nullable(),
});
// Согласования: формы совпадают с ES-75/77, вложенность не теряется в SDK.
export const approvalPersonSchema = z.object({ id: uuidSchema, display_name: z.string() });
export const workflowStaffCandidateSchema = approvalPersonSchema.extend({ substituted_by: approvalPersonSchema.nullable() });
export type WorkflowStaffCandidate = z.infer<typeof workflowStaffCandidateSchema>;
export const approvalDueInputSchema = z.object({ amount: z.number().int().min(1).max(32767), unit: z.enum(["hours", "working_days"]) }).strict();
export const approvalRouteStepInputSchema = z.object({
  mode: z.enum(["all", "any"]), due: approvalDueInputSchema.nullable().optional(),
  approver_ids: z.array(uuidSchema).min(1).max(10),
}).strict();
export const approvalRouteInputSchema = z.array(approvalRouteStepInputSchema).min(1).max(10);
export const approvalTemplateBodySchema = z.object({
  access_scope_id: uuidSchema, name: z.string().min(1).max(150), description: z.string().max(10000).nullable().optional(),
  document_type: z.enum(["incoming", "outgoing", "internal"]).nullable().optional(),
  internal_kind: z.enum(["order", "memo"]).nullable().optional(), is_active: z.boolean().optional(), steps: approvalRouteInputSchema,
}).strict();
export const approvalTemplateUpdateBodySchema = approvalTemplateBodySchema.omit({ access_scope_id: true });
export const approvalStartBodySchema = z.union([
  z.object({ template_id: uuidSchema }).strict(), z.object({ steps: approvalRouteInputSchema }).strict(),
]);
export const approvalDecisionBodySchema = z.object({ decision: z.enum(["approve", "return", "reject"]), comment: z.string().max(2000).nullable().optional() }).strict();
export const substitutionBodySchema = z.object({
  user_id: uuidSchema.nullable().optional(), substitute_id: uuidSchema, access_scope_id: uuidSchema,
  starts_at: timestampSchema, ends_at: timestampSchema, reason: z.enum(["vacation", "business_trip", "sick_leave", "other"]), note: z.string().max(500).nullable().optional(),
}).strict();
export const documentApprovalTemplateSchema = z.object({
  id: uuidSchema, name: z.string(), description: z.string().nullable(),
  updated_at: timestampSchema.nullable().optional(),
  access_scope: z.object({ id: uuidSchema, name: z.string() }),
  document_type: z.enum(["incoming", "outgoing", "internal"]).nullable(), internal_kind: z.enum(["order", "memo"]).nullable(), is_active: z.boolean(),
  steps: z.array(z.object({ position: z.number().int().positive(), mode: z.enum(["all", "any"]), due: approvalDueInputSchema.nullable(), approvers: z.array(approvalPersonSchema) })),
});
export const documentApprovalTaskSchema = z.object({
  id: uuidSchema, approver: approvalPersonSchema, routed_to: approvalPersonSchema.nullable(),
  state: z.enum(["pending", "approved", "returned", "rejected", "not_required", "cancelled"]), comment: z.string().nullable(),
  decided_by: approvalPersonSchema.nullable(), on_behalf_of: approvalPersonSchema.nullable(), decided_at: timestampSchema.nullable(),
});
export const approvalDueStateSchema = z.enum(["normal", "due_soon_24h", "due_soon_2h", "overdue", "completed"]);
export const documentApprovalSchema = z.object({
  id: uuidSchema, document_id: uuidSchema, status: z.enum(["in_progress", "returned", "approved", "rejected", "withdrawn"]), current_round: z.number().int().positive(),
  template: z.object({ id: uuidSchema, name: z.string() }).nullable(), started_by: approvalPersonSchema.nullable(), started_at: timestampSchema, finished_at: timestampSchema.nullable(),
  rounds: z.array(z.object({ round: z.number().int().positive(), steps: z.array(z.object({
    position: z.number().int().positive(), mode: z.enum(["all", "any"]), status: z.enum(["waiting", "active", "completed"]), opened_at: timestampSchema.nullable(), due_at: timestampSchema.nullable(), due_state: approvalDueStateSchema, tasks: z.array(documentApprovalTaskSchema),
  })) })),
});
export const documentApprovalInboxTaskSchema = z.object({
  id: uuidSchema, approval_id: uuidSchema, document: z.object({ id: uuidSchema, subject: z.string(), registration_number: z.string().nullable() }),
  round: z.number().int().positive(), step_position: z.number().int().positive(), mode: z.enum(["all", "any"]), due_at: timestampSchema.nullable(), due_state: approvalDueStateSchema,
  state: z.enum(["pending", "approved", "returned", "rejected", "not_required", "cancelled"]), redirected_from: approvalPersonSchema.nullable(),
});
export const documentSubstitutionSchema = z.object({
  id: uuidSchema, user: approvalPersonSchema.nullable(), substitute: approvalPersonSchema.nullable(), access_scope: z.object({ id: uuidSchema, name: z.string() }),
  starts_at: timestampSchema, ends_at: timestampSchema, reason: z.enum(["vacation", "business_trip", "sick_leave", "other"]), note: z.string().nullable(), status: z.enum(["scheduled", "active", "ended", "cancelled"]),
  created_by: approvalPersonSchema.nullable(), cancelled_by: approvalPersonSchema.nullable(), cancelled_at: timestampSchema.nullable(),
});
export const documentWorkflowCandidateSchema = userSummarySchema.extend({ substituted_by: approvalPersonSchema.nullable().optional() });
export const documentWorkflowAssigneeSchema = userSummarySchema.extend({ redirected_from: userSummarySchema.nullable().optional() });
export type ApprovalTemplate = z.infer<typeof documentApprovalTemplateSchema>;
export type DocumentApproval = z.infer<typeof documentApprovalSchema>;
export type ApprovalInboxTask = z.infer<typeof documentApprovalInboxTaskSchema>;
export type DocumentSubstitution = z.infer<typeof documentSubstitutionSchema>;
export type ApprovalRouteStepInput = z.infer<typeof approvalRouteStepInputSchema>;

export const documentWorkflowDocumentSchema = z.object({
  owner_id: uuidSchema.nullable().optional(),
  internal_kind: z.enum(["order", "memo"]).nullable().optional(),
  participants: z.array(z.object({ user_id: uuidSchema, role: z.string() })).optional(),
  recipient_ids: z.array(uuidSchema).optional(),
  visibility_reasons: z.array(z.enum(["owner", "assignee", "controller", "recipient", "participant", "scope_share", "organization_wide", "approver"])).optional(),
  unread_mail_count: z.number().int().nonnegative().optional(),
  unread_comment_count: z.number().int().nonnegative().optional(),
  first_unread_mail_id: uuidSchema.nullable().optional(),
  first_unread_comment_id: uuidSchema.nullable().optional(),
  has_discussion: z.boolean().optional(),
  has_new_data: z.boolean().optional(),
  id: uuidSchema,
  access_scope_id: uuidSchema,
  scope_ids: z.array(uuidSchema),
  type: z.enum(["incoming", "outgoing", "internal"]),
  category: documentWorkflowCategorySummarySchema.nullable(),
  folder: documentWorkflowFolderSummarySchema.nullable(),
  registration: documentRegistrationSchema.nullable(),
  received_at: timestampSchema,
  subject: z.string(),
  description: z.string().nullable(),
  counterparty: documentCounterpartySummarySchema.nullable(),
  sender_snapshot: z.record(jsonValueSchema).nullable(),
  incoming_number: z.string().nullable(),
  document_date: z.string().nullable(),
  // Колонки бумажного журнала (ES-58).
  copies_count: z.number().int().nullable().optional(),
  sheets_count: z.string().nullable().optional(),
  assignee_id: uuidSchema.nullable(),
  controller_id: uuidSchema.nullable(),
  // Те же люди карточками: в списке документов человека показывают именем.
  owner: userSummarySchema.nullable().optional(),
  assignee: documentWorkflowAssigneeSchema.nullable().optional(),
  controller: documentWorkflowAssigneeSchema.nullable().optional(),
  due_at: timestampSchema.nullable(),
  due_state: z.enum([
    "normal",
    "due_soon_24h",
    "due_soon_2h",
    "overdue",
    "completed",
  ]),
  status: z.enum([
    "received",
    "registered",
    "read",
    "in_progress",
    "completed",
    "cancelled",
  ]),
  priority: z.enum(["low", "normal", "high", "critical"]),
  completed_at: timestampSchema.nullable(),
  completed_by: uuidSchema.nullable(),
  unread: z.boolean(),
  read_at: timestampSchema.nullable(),
  latest_external_at: timestampSchema.nullable(),
  latest_internal_at: timestampSchema.nullable(),
  capabilities: z.record(z.boolean()),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});
export const documentWorkflowEntitySchema = z
  .object({ id: uuidSchema })
  .passthrough();
export const documentWorkflowMailOutboxSchema = z.object({
  id: uuidSchema,
  document_id: uuidSchema,
  status: z.enum([
    "draft",
    "queued",
    "sending",
    "sent",
    "failed",
    "cancelled",
    "delivery_uncertain",
  ]),
  message_id: z.string().nullable(),
  in_reply_to: z.string().nullable(),
  references: z.array(z.string()),
  recipients: z.object({
    to: z.array(z.string().email()),
    cc: z.array(z.string().email()),
    bcc: z.array(z.string().email()),
  }),
  subject: z.string(),
  attempts: z.number().int().nonnegative(),
  sent_at: timestampSchema.nullable(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});
export const documentWorkflowParticipantSchema = z.object({
  id: uuidSchema.nullable(), user_id: uuidSchema,
  user: userSummarySchema.nullable(),
  role: z.enum(["owner", "assignee", "controller", "recipient", "participant"]),
  is_explicit: z.boolean(), can_remove: z.boolean(), note: z.string().nullable(), added_at: z.string().nullable(), added_by: uuidSchema.nullable(),
});
export const documentWorkflowAssignmentSchema = z.object({
  id: uuidSchema, assignee_id: uuidSchema.nullable(), assigned_by: uuidSchema.nullable(),
  reason: z.string().nullable(), assigned_at: z.string(), ended_at: z.string().nullable(),
});
export const documentWorkflowReadSchema = z.object({
  id: uuidSchema, user_id: uuidSchema.nullable(),
  // Кто прочитал — вопрос про человека, а не про идентификатор.
  user: userSummarySchema.nullable().optional(),
  read_at: z.string(),
});
export const documentWorkflowRelationSchema = z.object({
  id: uuidSchema, related_document_id: uuidSchema, related_document_subject: z.string(), related_document_number: z.string().nullable(), type: z.enum(["related", "parent", "child", "duplicate", "response_to", "answered_by"]), note: z.string().nullable(), created_at: z.string(),
});
export const documentWorkflowHistoryExportSchema = z.object({
  id: uuidSchema, document_id: uuidSchema, format: z.enum(["json", "csv"]), status: z.enum(["queued", "processing", "completed", "failed"]), error_code: z.string().nullable(), created_at: z.string(), updated_at: z.string(),
});
export const documentWorkflowCommentSchema = z.object({
  id: uuidSchema, document_id: uuidSchema.nullable(), author_id: uuidSchema.nullable(),
  // Обсуждение читают как переписку: автор назван именем.
  author: userSummarySchema.nullable().optional(),
  reply_to_comment_id: uuidSchema.nullable(),
  mentioned_user_ids: z.array(uuidSchema), attachment_file_ids: z.array(uuidSchema),
  text: z.string().nullable(), deleted: z.boolean(),
  // Не прочитан текущим пользователем: комментарий за курсором прочтения, не свой и не удалённый.
  unread: z.boolean().optional(),
  edit_version: z.number().int(), created_at: z.string(), updated_at: z.string(),
});
export const documentWorkflowTimelineSchema = z.object({
  id: z.string(), version: z.literal(1), type: z.string(), channel: z.enum(["external", "internal", "system"]),
  document_id: uuidSchema, occurred_at: z.string(), actor: z.object({ type: z.string(), id: uuidSchema.nullable(), name: z.string().nullable() }),
  subject_id: z.string().nullable(), payload: z.record(jsonValueSchema), redacted_fields: z.array(z.string()),
});
export const documentWorkflowFileProvenanceSchema = z.object({
  type: z.string(),
  // Ссылка на источник — это объект, а не строка: у вложения письма там
  // идентификаторы письма и самого вложения, у ручной привязки её нет вовсе.
  // Форма зависит от происхождения файла, поэтому описана свободным объектом.
  reference: z.record(jsonValueSchema).nullable(),
  linked_by: uuidSchema.nullable(),
  linked_at: timestampSchema.nullable(),
});
export const documentWorkflowFileVersionSchema = z.object({
  id: uuidSchema,
  document_file_id: uuidSchema.nullable(),
  version_number: z.number().int().positive(),
  file_id: uuidSchema.nullable(),
  base_version_id: uuidSchema.nullable(),
  sha256: z.string().nullable(),
  size: z.number().int().nonnegative().nullable(),
  detected_mime: z.string().nullable(),
  source: z.string(),
  provenance: documentWorkflowFileProvenanceSchema,
  scan_status: z.string(),
  available_actions: z.array(z.string()),
  reference_count: z.number().int().nonnegative(),
  comment: z.string().nullable(),
  signature_state: z.enum(["signed", "unsigned"]),
  created_by: uuidSchema.nullable(),
  created_at: timestampSchema,
});
export const documentWorkflowFileSchema = z.object({
  id: uuidSchema,
  document_id: uuidSchema.nullable(),
  display_name: z.string(),
  current_version: documentWorkflowFileVersionSchema.nullable(),
  version_count: z.number().int().nonnegative(),
  provenance: documentWorkflowFileProvenanceSchema,
  scan_status: z.string(),
  available_actions: z.array(z.string()),
  can_delete: z.boolean(),
  created_at: timestampSchema,
  updated_at: timestampSchema,
});
export const documentWorkflowCounterpartyContactSchema = z.object({
  id: uuidSchema,
  name: z.string().nullable(),
  email: z.string(),
  position: z.string().nullable(),
  is_primary: z.boolean(),
});
export const documentWorkflowCounterpartySchema = documentWorkflowEntitySchema.extend({
  name: z.string(),
  inn: z.string().nullable(),
  kpp: z.string().nullable(),
  notes: z.string().nullable(),
  domains: z.array(z.string()),
  contacts: z.array(documentWorkflowCounterpartyContactSchema),
});
// Правило определения отправителя. Меньший приоритет сильнее. Время отдаётся
// так, как оно записано в базе, поэтому это строка, а не строго ISO 8601.
export const documentWorkflowSenderRuleSchema = z.object({
  id: uuidSchema,
  counterparty_id: uuidSchema,
  match_type: z.enum(["email", "domain", "pattern"]),
  pattern: z.string(),
  priority: z.number().int().nonnegative(),
  created_at: z.string(),
});
// Кандидат в отправители письма: тот же набор, по которому сервер выбирает
// контрагента. Меньший приоритет совпадения — более сильное основание.
export const documentWorkflowSenderCandidateSchema = z.object({
  counterparty_id: uuidSchema,
  name: z.string(),
  inn: z.string().nullable(),
  kpp: z.string().nullable(),
  match_method: z.string(),
  match_priority: z.number().int().nonnegative(),
  is_current: z.boolean(),
});
export const documentWorkflowMailboxSchema = documentWorkflowEntitySchema.extend({
  // Публичный идентификатор организации (КПО), в которой заведён ящик.
  access_scope_id: uuidSchema,
  name: z.string(),
  email: z.string().email(),
  imap: z.object({
    host: z.string(),
    port: z.number().int().positive(),
    encryption: z.enum(["ssl", "tls", "starttls"]),
    folder: z.string(),
  }),
  smtp: z.object({
    host: z.string(),
    port: z.number().int().positive(),
    encryption: z.enum(["ssl", "tls", "starttls"]),
  }),
  username: z.string(),
  is_system_sender: z.boolean().optional(),
  has_credential_reference: z.boolean(),
  enabled: z.boolean(),
  status: z.string(),
  last_successful_sync_at: timestampSchema.nullable(),
  last_error_code: z.string().nullable(),
});
export const documentWorkflowMailboxConnectionSchema = z.object({
  mailbox_id: uuidSchema,
  direction: z.enum(["imap", "smtp", "both"]),
  ok: z.boolean(),
  code: z.string().nullable(),
  checked_at: timestampSchema,
});
export const documentWorkflowMailSyncStateSchema = z.object({
  mailbox_id: uuidSchema,
  folder: z.string(),
  uidvalidity: z.number().int().nullable(),
  discovery_uid: z.number().int().nonnegative(),
  imported_uid: z.number().int().nonnegative(),
  status: z.string(),
  last_started_at: timestampSchema.nullable(),
  last_completed_at: timestampSchema.nullable(),
});
export const documentWorkflowMailSyncRunSchema = z.object({
  id: uuidSchema,
  mailbox_id: uuidSchema,
  folder: z.string(),
  from_uid: z.number().int().nullable().optional(),
  status: z.string(),
  discovered_count: z.number().int().nonnegative(),
  imported_count: z.number().int().nonnegative(),
  started_at: timestampSchema.nullable().optional(),
  completed_at: timestampSchema.nullable().optional(),
  created_at: timestampSchema,
});
// Ошибка импорта письма. Ядро отдаёт только безопасное сообщение: текстов писем
// и секретов в ответе нет. В карточке добавляются контекст и заметка о разборе.
export const documentWorkflowImportFailureSchema = z.object({
  id: uuidSchema,
  mailbox_id: uuidSchema.nullable(),
  stage: z.string(),
  status: z.string(),
  retryable: z.boolean(),
  error_code: z.string(),
  safe_message: z.string(),
  attempts: z.number().int().nonnegative(),
  created_at: z.string(),
  updated_at: z.string(),
});
export const documentWorkflowImportFailureDetailSchema = documentWorkflowImportFailureSchema.extend({
  safe_context: z.record(jsonValueSchema),
  resolution_note: z.string().nullable(),
});
export const documentWorkflowMailMessageSchema = z.object({
  thread_id: uuidSchema.nullable(),
  unread: z.boolean(),
  from: z.object({ email: z.string(), name: z.string().nullable() }).nullable(),
  to: z.array(z.object({ email: z.string(), name: z.string().nullable() })),
  cc: z.array(z.object({ email: z.string(), name: z.string().nullable() })),
  bcc: z.array(z.object({ email: z.string(), name: z.string().nullable() })).optional(),
  has_attachments: z.boolean(), attachment_count: z.number().int().nonnegative(),
  text_body: z.string().nullable().optional(), sanitized_html_body: z.string().nullable().optional(),
  // Как определён отправитель: unresolved и conflict ждут решения человека.
  // Приоритет отдаётся только в подробном представлении письма.
  counterparty_id: uuidSchema.nullable(),
  sender_resolution_method: z.string().nullable(),
  sender_resolution_priority: z.number().int().nullable().optional(),
  id: uuidSchema,
  direction: z.enum(["incoming", "outgoing"]),
  message_id: z.string().nullable(),
  in_reply_to: z.string().nullable(),
  references: z.array(z.string()),
  subject: z.string().nullable(),
  size: z.number().int().nonnegative(),
  sha256: z.string(),
  sent_at: timestampSchema.nullable(),
  received_at: timestampSchema.nullable(),
  imported_at: timestampSchema.nullable(),
});
export const documentWorkflowMailThreadSchema = z.object({ id: uuidSchema, normalized_subject: z.string().nullable(), message_count: z.number().int().nonnegative(), unread_count: z.number().int().nonnegative() });
export const documentWorkflowSignatureCapabilitiesSchema = z.object({
  enabled: z.boolean(),
  server_verifier_available: z.boolean(),
  profiles: z.array(z.enum(["CAdES-BES", "CAdES-T", "CAdES-X Long Type 1"])),
  request_ttl_seconds: z.number().int().positive(),
  verification_mode: z.enum(["server", "fail_closed"]),
});
// Запрос подписи. Одноразовое значение nonce приходит только в ответе на
// создание: повторное чтение запроса его не раскрывает. Время отдаётся так, как
// оно записано в базе, поэтому это строки, а не строго ISO 8601.
export const documentWorkflowSignatureRequestSchema = z.object({
  id: uuidSchema,
  version_id: uuidSchema,
  cades_profile: z.enum(["CAdES-BES", "CAdES-T", "CAdES-X Long Type 1"]),
  expected_sha256: z.string(),
  expires_at: z.string(),
  used_at: z.string().nullable(),
  created_at: z.string(),
  nonce: z.string().optional(),
});
// Отделённая подпись версии. Тело подписи сервер не отдаёт: только хеш и
// безопасные сведения сертификата.
export const documentWorkflowSignatureSchema = z.object({
  id: uuidSchema,
  version_id: uuidSchema,
  signed_by: uuidSchema,
  cades_profile: z.enum(["CAdES-BES", "CAdES-T", "CAdES-X Long Type 1"]),
  signature_sha256: z.string(),
  certificate: z.object({
    thumbprint: z.string().nullable(),
    subject: z.string().nullable(),
    issuer: z.string().nullable(),
    valid_from: z.string().nullable(),
    valid_to: z.string().nullable(),
  }),
  verification_state: z.string(),
  signed_at: z.string(),
});
export const documentWorkflowSignatureFileSchema = z.object({
  filename: z.string(),
  mime_type: z.string(),
  content: z.string(),
});
export const documentWorkflowSignatureStampSchema = z.object({
  id: uuidSchema,
  version_id: uuidSchema,
  status: z.enum(["queued", "processing", "completed", "failed"]),
  error_code: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export const documentWorkflowSignatureVerificationSchema = z.object({
  id: uuidSchema,
  signature_id: uuidSchema,
  status: z.enum(["verified", "invalid"]),
  verifier: z.string(),
  error_code: z.string().nullable(),
  verified_at: z.string().nullable(),
});
export const documentWorkflowDashboardSchema = z.object({
  counts_by_status: z.record(z.number().int().nonnegative()),
  unread: z.number().int().nonnegative(),
  overdue: z.number().int().nonnegative(),
  generated_at: timestampSchema,
});
export const documentWorkflowExecutorReportSchema = z.object({
  rows: z.array(
    z.object({
      assignee_id: uuidSchema.nullable(),
      assignee: userSummarySchema.nullable().optional(),
      status: z.enum([
        "received",
        "registered",
        "read",
        "in_progress",
        "completed",
        "cancelled",
      ]),
      count: z.number().int().nonnegative(),
    }),
  ),
});
export const documentWorkflowDeadlineReportSchema = z.object({
  rows: z.array(
    z.object({
      document_id: uuidSchema,
      subject: z.string(),
      due_at: timestampSchema.nullable(),
      due_state: z.enum([
        "normal",
        "due_soon_24h",
        "due_soon_2h",
        "overdue",
        "completed",
      ]),
      assignee_id: uuidSchema.nullable(),
    }),
  ),
});
export const documentWorkflowSignedDownloadSchema = z.object({
  url: z.string().url(),
  expires_at: timestampSchema,
  sha256: z.string().length(64).optional(),
  filename: z.string().optional(),
});
export function documentWorkflowPageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: z.array(item), meta: documentWorkflowPageMetaSchema });
}
export function documentWorkflowCursorSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: z.array(item), meta: documentWorkflowCursorMetaSchema });
}

export const publicNewsCategorySchema = z.object({
  id: uuidSchema,
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  sort_order: z.number().int().nonnegative(),
  is_active: z.boolean(),
  seo: z.object({
    title: z.string().nullable(),
    description: z.string().nullable(),
  }),
});
export const siteMediaSchema = z.object({
  preview_url: z.string().nullable().optional(),
  processing_status: z.enum(["pending", "ready", "failed"]).optional(),
  processing_error: z.string().nullable().optional(),
  id: uuidSchema,
  file_id: uuidSchema,
  url: z.string().nullable(),
  alt: z.string().nullable(),
  mime_type: z.string(),
  size: z.number().int().nonnegative(),
  width: z.number().int().nonnegative().nullable(),
  height: z.number().int().nonnegative().nullable(),
  status: z.enum(["pending", "ready", "rejected", "expired"]),
});
export const siteContentMediaLinkSchema = z.object({
  purpose: z.enum(["inline", "gallery"]), sort_order: z.number().int().nonnegative(),
  source: z.enum(["manual", "content"]), media: siteMediaSchema,
});
export const publicSitemapEntrySchema = z.object({
  type: z.enum(["news", "page"]), slug: z.string(), lastmod: timestampSchema.nullable(),
});
export const archivedSiteCategorySchema = publicNewsCategorySchema.extend({archived_at: timestampSchema.nullable()});
export const siteSeoSchema = z.object({
  title: z.string().nullable(),
  description: z.string().nullable(),
  og_image: siteMediaSchema.nullable().optional(),
});
export const siteContentBlockSchema = z.union([
  z.string(),
  z.record(jsonValueSchema),
]);
export const publicNewsItemSchema = z.object({
  id: uuidSchema,
  slug: z.string(),
  title: z.string(),
  excerpt: z.string(),
  content_format: z.enum(["html", "json"]),
  content_html: z.string().nullable(),
  content: z.array(siteContentBlockSchema).nullable(),
  cover: siteMediaSchema.nullable(),
  published_at: timestampSchema.nullable(),
  updated_at: timestampSchema,
  category: publicNewsCategorySchema.nullable(),
  author: z.string().nullable(),
  is_pinned: z.boolean(),
  seo: siteSeoSchema,
});
export const adminSiteNewsSchema = publicNewsItemSchema.extend({
  media: z.array(siteContentMediaLinkSchema).optional(),
  status: z.enum(["draft", "scheduled", "published", "archived"]),
  scheduled_at: timestampSchema.nullable(),
  version: z.number().int().positive(),
  created_by: uuidSchema.nullable(),
  updated_by: uuidSchema.nullable(),
  published_by: uuidSchema.nullable(),
  created_at: timestampSchema,
});
export const publicPageSchema = z.object({
  id: uuidSchema,
  key: z.string(),
  slug: z.string(),
  title: z.string(),
  content_format: z.enum(["html", "json"]),
  content_html: z.string().nullable(),
  content: z.array(siteContentBlockSchema).nullable(),
  published_at: timestampSchema.nullable(),
  updated_at: timestampSchema,
  seo: siteSeoSchema.omit({ og_image: true }),
});
export const adminSitePageSchema = publicPageSchema.extend({
  media: z.array(siteContentMediaLinkSchema).optional(),
  status: z.enum(["draft", "published", "archived"]),
  version: z.number().int().positive(),
  created_by: uuidSchema.nullable(),
  updated_by: uuidSchema.nullable(),
  published_by: uuidSchema.nullable(),
  created_at: timestampSchema,
});
// Слайд главной страницы. Картинка хранится медиа-записью сайта, поэтому
// задаётся её идентификатором; готовый адрес сервер подставляет сам и только
// для тех слайдов, чей файл действительно лежит в хранилище.
export const heroSlideSchema = z.object({
  id: z.string().uuid(),
  eyebrow: z.string().max(80),
  title: z.string().max(255),
  description: z.string().max(1000),
  href: z.string().max(2000),
  cta: z.string().max(80),
  image_media_id: z.string().uuid(),
  image_url: z.string().nullable().optional(),
});
export const publicSiteSettingsSchema = z.object({
  site_name: z.string(),
  legal_name: z.string(),
  phone: z.string(),
  email: z.string().email(),
  address: z.string(),
  working_hours: z.string(),
  map_url: z.string().url().nullable(),
  default_seo_title: z.string(),
  default_seo_description: z.string(),
  default_og_image_url: z.string().url().nullable(),
  hero_slides: z.array(heroSlideSchema).max(8).optional(),
});
// Инструкция сотрудника: копия страницы Confluence. Ядро переводит страницу в
// небольшое дерево блоков, а экран строит из него обычные элементы — чужой
// HTML в интерфейс не попадает.
export const userGuideInlineSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("text"),
    text: z.string(),
    marks: z.array(
      z.enum(["strong", "em", "code", "strike", "underline", "sub", "sup"]),
    ),
    href: z.string().nullable(),
  }),
  z.object({ type: z.literal("break") }),
  z.object({
    type: z.literal("status"),
    text: z.string(),
    tone: z.enum(["neutral", "info", "note", "success", "warning", "error"]),
  }),
]);
export type UserGuideInline = z.infer<typeof userGuideInlineSchema>;
export type UserGuideTableCell = {
  header: boolean;
  colspan: number;
  rowspan: number;
  blocks: UserGuideBlock[];
};
export type UserGuideBlock =
  | { type: "heading"; id: string; level: number; text: string }
  | { type: "paragraph"; inlines: UserGuideInline[] }
  | { type: "list"; ordered: boolean; start: number; items: UserGuideBlock[][] }
  | { type: "quote"; blocks: UserGuideBlock[] }
  | {
      type: "callout";
      tone: "info" | "note" | "success" | "warning" | "error";
      blocks: UserGuideBlock[];
    }
  | { type: "expand"; title: string; blocks: UserGuideBlock[] }
  | { type: "code"; language: string | null; text: string }
  | { type: "table"; rows: UserGuideTableCell[][] }
  | {
      type: "image";
      image_id: string | null;
      alt: string;
      width: number | null;
      height: number | null;
      caption: string | null;
    }
  | { type: "rule" };
export const userGuideBlockSchema: z.ZodType<UserGuideBlock> = z.lazy(() =>
  z.discriminatedUnion("type", [
    z.object({
      type: z.literal("heading"),
      id: z.string().min(1),
      level: z.number().int().min(2).max(4),
      text: z.string(),
    }),
    z.object({
      type: z.literal("paragraph"),
      inlines: z.array(userGuideInlineSchema),
    }),
    z.object({
      type: z.literal("list"),
      ordered: z.boolean(),
      start: z.number().int().positive(),
      items: z.array(z.array(userGuideBlockSchema)),
    }),
    z.object({
      type: z.literal("quote"),
      blocks: z.array(userGuideBlockSchema),
    }),
    z.object({
      type: z.literal("callout"),
      tone: z.enum(["info", "note", "success", "warning", "error"]),
      blocks: z.array(userGuideBlockSchema),
    }),
    z.object({
      type: z.literal("expand"),
      title: z.string(),
      blocks: z.array(userGuideBlockSchema),
    }),
    z.object({
      type: z.literal("code"),
      language: z.string().nullable(),
      text: z.string(),
    }),
    z.object({
      type: z.literal("table"),
      rows: z.array(
        z.array(
          z.object({
            header: z.boolean(),
            colspan: z.number().int().positive(),
            rowspan: z.number().int().positive(),
            blocks: z.array(userGuideBlockSchema),
          }),
        ),
      ),
    }),
    z.object({
      type: z.literal("image"),
      image_id: z.string().uuid().nullable(),
      alt: z.string(),
      width: z.number().int().positive().nullable(),
      height: z.number().int().positive().nullable(),
      caption: z.string().nullable(),
    }),
    z.object({ type: z.literal("rule") }),
  ]),
);
export const userGuideHeadingSchema = z.object({
  id: z.string().min(1),
  title: z.string(),
  level: z.number().int().min(2).max(4),
});
// Ссылки подписываются на каждый ответ и живут недолго: по ним браузер берёт
// байты скриншотов прямо из хранилища.
export const userGuideImageSchema = z.object({
  url: z.string().url(),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
});
export const userGuideSchema = z.object({
  // pending — копия ещё собирается, unavailable — интеграция не настроена.
  status: z.enum(["ready", "pending", "unavailable"]),
  title: z.string().nullable(),
  source_url: z.string().url().nullable(),
  source_version: z.number().int().positive().nullable(),
  source_updated_at: timestampSchema.nullable(),
  synced_at: timestampSchema.nullable(),
  checked_at: timestampSchema.nullable(),
  // Последняя проверка Confluence не удалась: копия может отставать.
  sync_failing: z.boolean(),
  // Классификация причины последнего сбоя — не секрет, помогает
  // диагностировать настройку интеграции без доступа к логам сервера.
  error_code: z.string().nullable(),
  headings: z.array(userGuideHeadingSchema),
  blocks: z.array(userGuideBlockSchema),
  images: z.record(z.string(), userGuideImageSchema),
});
export function dataSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: item });
}
export function collectionSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({ data: z.array(item) });
}
export function paginatedSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    data: z.array(item),
    meta: pageMetaSchema,
    links: pageLinksSchema,
  });
}
export function cursorPageSchema<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    data: z.array(item),
    links: cursorLinksSchema,
    meta: cursorMetaSchema,
  });
}

export type IdentityUser = z.infer<typeof identityUserSchema>;
export type IdentitySession = z.infer<typeof identitySessionSchema>;
export type AuthenticatedResponse = z.infer<typeof authenticatedResponseSchema>;
export type LoginResponse = z.infer<typeof loginResponseSchema>;
export type AccessScope = z.infer<typeof accessScopeSchema>;
export type Department = z.infer<typeof departmentSchema>;
export type Membership = z.infer<typeof membershipSchema>;
export type Permission = z.infer<typeof permissionSchema>;
export type Role = z.infer<typeof roleSchema>;
export type AccessRightsTransferPlan = z.infer<typeof accessRightsTransferPlanSchema>;
export type AccessRightsTransferResult = z.infer<typeof accessRightsTransferResultSchema>;
export type AccessChange = z.infer<typeof accessChangeSchema>;
export type UserChange = z.infer<typeof userChangeSchema>;
export type CriticalAdministrator = z.infer<typeof criticalAdministratorSchema>;
export type AdminUser = z.infer<typeof adminUserSchema>;
export type HierarchyNode = z.infer<typeof hierarchyNodeSchema>;
export type HierarchyReach = z.infer<typeof hierarchyReachSchema>;
export type AuditEvent = z.infer<typeof auditEventSchema>;
export type AuditExport = z.infer<typeof auditExportSchema>;
export type FilePurpose = z.infer<typeof filePurposeSchema>;
export type FileRecord = z.infer<typeof fileRecordSchema>;
export type FileFolder = z.infer<typeof fileFolderSchema>;
export type FileFolderScope = z.infer<typeof fileFolderScopeSchema>;
export type FileUsage = z.infer<typeof fileUsageSchema>;
export type SignedUpload = z.infer<typeof signedUploadSchema>;
export type SignedDownload = z.infer<typeof signedDownloadSchema>;
export type SignedPreview = z.infer<typeof signedPreviewSchema>;
export type ModuleDefinition = z.infer<typeof moduleDefinitionSchema>;
export type ModuleNavigationItem = z.infer<typeof moduleNavigationItemSchema>;
export type ModuleSettings = z.infer<typeof moduleSettingsSchema>;
export type SystemNotification = z.infer<typeof systemNotificationSchema>;
export type NotificationPreference = z.infer<typeof notificationPreferenceSchema>;
export type PushSubscription = z.infer<typeof pushSubscriptionSchema>;
export type RealtimeConfig = z.infer<typeof realtimeConfigSchema>;
export type UserGuide = z.infer<typeof userGuideSchema>;
export type UserGuideHeading = z.infer<typeof userGuideHeadingSchema>;
export type UserGuideImage = z.infer<typeof userGuideImageSchema>;
export type QueueHealth = z.infer<typeof queueHealthSchema>;
export type QueueState = z.infer<typeof queueStateSchema>;
export type DocumentWorkflowFile = z.infer<typeof documentWorkflowFileSchema>;
export type DocumentWorkflowFileVersion = z.infer<
  typeof documentWorkflowFileVersionSchema
>;
export type OutboxHealth = z.infer<typeof outboxHealthSchema>;
